// The add-app wizard's client-side state (app-engineering §2: model UI state as a
// discriminated union mirroring the server state machine; §4: a gated flow is
// server-gated and resumable, never UI-only). The wizard only ever holds an `appId`
// once the App record exists server-side (GET /v1/apps/:id is the source of truth for
// what is actually unlocked) - draft fields before that point (type/origin) are pure
// local UI state with no server counterpart yet.
//
// RESOLVED CONFLICT (flagged for the tech-lead, cited against the contract, never
// guessed against apps/api's in-flight code): docs/design/specs/slice-2-onboarding.md's
// cross-screen flow map (§3, §2.2) orders screens type(1) -> git-host(3) -> intent(2) ->
// cloud(4), but this lane's dispatch brief states `POST /v1/apps` captures
// "type existing/imported/new + intent + intentFreeText" together, and the shared `App`
// schema has NON-NULLABLE `intent` (packages/shared/src/model/apps.ts:36) while
// `GitHostInstallUrlRequest` REQUIRES an existing `appId` (ports/git-host.ts:96) - so an
// App record cannot exist (intent unset) before intent is known, yet git-host connect
// cannot run without an App record. These two requirements are mutually exclusive under
// the spec's stated order. Resolved in favor of the frozen data shape (the literal
// implementability constraint) over the UX flow diagram: this app orders the wizard
// type -> intent -> connect(git-host) -> cloud -> route -> sdk (screens in their own
// numeric order), creating the App record only once type+intent are both known.
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import type { AppId, AppOrigin, AppType } from "@nightcrew/shared";

const STORAGE_KEY = "nightcrew.wizard.appId";

export interface WizardDraft {
  readonly type: AppType | null;
  readonly origin: AppOrigin | null;
  readonly appId: AppId | null;
}

interface WizardContextValue {
  readonly draft: WizardDraft;
  readonly setType: (type: AppType, origin: AppOrigin) => void;
  readonly setAppId: (appId: AppId) => void;
  readonly reset: () => void;
}

const WizardContext = createContext<WizardContextValue | null>(null);

const readStoredAppId = (): AppId | null => {
  if (typeof sessionStorage === "undefined") return null;
  const value = sessionStorage.getItem(STORAGE_KEY);
  return value === null || value.length === 0 ? null : (value as AppId);
};

export const WizardProvider = ({
  children,
}: {
  readonly children: ReactNode;
}): React.JSX.Element => {
  const [draft, setDraft] = useState<WizardDraft>(() => ({
    type: null,
    origin: null,
    appId: readStoredAppId(),
  }));

  const setType = useCallback((type: AppType, origin: AppOrigin) => {
    setDraft((prev) => ({ ...prev, type, origin }));
  }, []);

  const setAppId = useCallback((appId: AppId) => {
    if (typeof sessionStorage !== "undefined") sessionStorage.setItem(STORAGE_KEY, appId);
    setDraft((prev) => ({ ...prev, appId }));
  }, []);

  const reset = useCallback(() => {
    if (typeof sessionStorage !== "undefined") sessionStorage.removeItem(STORAGE_KEY);
    setDraft({ type: null, origin: null, appId: null });
  }, []);

  const value = useMemo(
    () => ({ draft, setType, setAppId, reset }),
    [draft, setType, setAppId, reset],
  );

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
};

export const useWizard = (): WizardContextValue => {
  const ctx = useContext(WizardContext);
  if (ctx === null) throw new Error("useWizard must be used inside a WizardProvider");
  return ctx;
};

/** Pure gate: the git-host/cloud/route/sdk steps unlock only once the App record exists
 * (type + intent both captured server-side). Exported standalone so it is unit-testable
 * with no rendering. */
export const canAccessPostIntentStep = (draft: Pick<WizardDraft, "appId">): boolean =>
  draft.appId !== null;

/** Redirects to the type picker whenever a later wizard step is opened before the App
 * record exists - the D33 gate: intent (bundled into app creation) blocks every
 * downstream step, enforced here by simply having nothing to render without an appId. */
export const RequireWizardApp = ({
  children,
}: {
  readonly children: ReactNode;
}): React.JSX.Element => {
  const { draft } = useWizard();
  const location = useLocation();
  if (!canAccessPostIntentStep(draft)) {
    return <Navigate to="/apps/new" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
};
